import { CircularProgress, Tooltip } from '@mui/material'
import { useNonSuperUsers } from 'api/admin/admin.api'
import { useImpersonateMutation } from 'api/auth/auth.api'
import React, { ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Button } from 'ui/atoms'
import SearchField from 'ui/atoms/SearchField/SearchField'
import Datetime from 'utils/datetime/datetime'
import styles from '../ImpersonateUsersView.module.less'

function getUserSystemsLabel(user: User): string {
  const systems = user?.system_roles?.reduce((acc: string[], curr) => {
    if (!acc.includes(curr.system_name)) acc.push(curr.system_name)
    return acc
  }, [])
  return systems?.join(', ') || ''
}

function extractUniqueRoleNames(systemRoles: User['system_roles']): string {
  if (!systemRoles?.length) return ''
  const uniqueRoles = systemRoles.reduce((acc: string[], curr) => {
    if (!acc.includes(curr.role)) acc.push(curr.role)
    return acc
  }, [])
  return uniqueRoles.join(', ')
}

// Separate heavy list rendering from search input to keep typing snappy.
const UserRows = React.memo(function UserRows({
  users,
  loadingUsers,
  onImpersonate,
  impersonating,
  t,
}: {
  users: User[]
  loadingUsers: boolean
  onImpersonate: (id: number) => void
  impersonating: boolean
  t: (k: string) => string
}) {
  return (
    <div className={styles.ImpersonateUsersView_List}>
      {loadingUsers && <CircularProgress size={32} />}
      {!loadingUsers &&
        users.map((u) => (
          <div key={u.id} className={styles.ImpersonateUsersView_UserRow}>
            <div className={styles.ImpersonateUsersView_UserMeta}>
              <span className={styles.ImpersonateUsersView_Name}>
                {u.first_name || u.last_name ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : '-'}
              </span>
              <span className={styles.ImpersonateUsersView_Email}>{u.email}</span>
              {u.system_roles?.length ? (
                <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>{extractUniqueRoleNames(u.system_roles)}</span>
              ) : null}
              <Tooltip title={getUserSystemsLabel(u)}>
                <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>
                  {u.primary_system?.display_name || '-'} •{' '}
                  {Datetime.toLocalTime(u.created_at as ISODateTime, 'onlyDate')}
                </span>
              </Tooltip>
            </div>
            <div className={styles.ImpersonateUsersView_Actions}>
              <Button primary icon="fal fa-user-secret" disabled={impersonating} onClick={() => onImpersonate(u.id)}>
                {t('Impersonate')}
              </Button>
            </div>
          </div>
        ))}
      {!loadingUsers && !users.length && <p>{t('No users match your search')}</p>}
    </div>
  )
})

export default function ImpersonateUserList(): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchInput, setSearchInput] = useState<string>('')
  const [searchInputFieldValue, setSearchInputFieldValue] = useState<string>('')

  // Always fetch all users (backend returns all when query omitted) and filter client-side
  const { data: usersRaw = [], isPending: loadingUsers } = useNonSuperUsers('')
  const users = useMemo(() => usersRaw, [usersRaw])
  const { mutateAsync: impersonate, isPending: impersonating } = useImpersonateMutation()

  const handleImpersonate = useCallback(
    async (id: number) => {
      await impersonate({ userId: id })
      navigate('/startpage', { replace: true })
      window.location.reload()
    },
    [impersonate, navigate]
  )

  const updateUserList = useCallback(() => {
    setSearchInput(searchInputFieldValue)
  }, [searchInputFieldValue])

  // Preprocess and cache normalized user data for efficient filtering
  const normalizedUsers = useMemo(() => {
    return users.map((u) => {
      const parts: string[] = []
      if (u.first_name || u.last_name) parts.push(`${u.first_name || ''} ${u.last_name || ''}`)
      if (u.email) parts.push(u.email)
      if ((u as any).organization_name) parts.push((u as any).organization_name)
      if (u.primary_system?.display_name) parts.push(u.primary_system.display_name)
      if (u.system_roles?.length) {
        // Deduplicate system names and roles for clarity
        const uniqueSystemNames = Array.from(new Set(u.system_roles.map((r) => r.system_name))).join(' ')
        const uniqueRoles = Array.from(new Set(u.system_roles.map((r) => r.role || ''))).join(' ')
        parts.push(uniqueSystemNames)
        parts.push(uniqueRoles)
        parts.push(extractUniqueRoleNames(u.system_roles))
      }
      return {
        ...u,
        _haystack: parts.join(' ').toLowerCase().trim(),
      }
    })
  }, [users])

  const filteredUsers = useMemo(() => {
    const q = searchInput.toLowerCase().trim()
    if (!q) return normalizedUsers
    const tokens = q.split(/\s+/).filter(Boolean)
    if (!tokens.length) return normalizedUsers
    return normalizedUsers.filter((u) => tokens.every((tk) => u._haystack.includes(tk)))
  }, [normalizedUsers, searchInput])

  return (
    <>
      <div className={styles.ImpersonateUsersView_SearchContainer}>
        <SearchField
          label={t('Search users')}
          value={searchInputFieldValue}
          onChange={(s) => setSearchInputFieldValue(String(s))}
          onClear={() => {
            setSearchInputFieldValue('')
            setSearchInput('')
          }}
          onEnter={() => updateUserList()}
          inputProps={{ 'aria-label': t('Search users') as string }}
        />
        <Button
          className={styles.ImpersonateUsersView_SearchButton}
          primary
          disabled={loadingUsers}
          onClick={() => {
            updateUserList()
          }}
        >
          <>
            <i className="fal fa-search" style={{ marginRight: 4 }} /> {t('Search')}
          </>
        </Button>
      </div>
      <UserRows
        users={filteredUsers}
        loadingUsers={loadingUsers}
        onImpersonate={handleImpersonate}
        impersonating={impersonating}
        t={t}
      />
    </>
  )
}
